import { useState, useEffect } from "react";
import { siteSettings } from "../services/api";

/**
 * Custom hook to fetch and manage site settings
 * @param key The setting key to fetch
 * @returns Object with the setting value, loading state, and error
 */
export function useSiteSetting(key: string) {
  const [value, setValue] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSetting = async () => {
      try {
        setLoading(true);
        const response: any = await siteSettings.get(key);
        if (response?.data) {
          setValue(response.data);
        }
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load setting");
        setValue(null);
      } finally {
        setLoading(false);
      }
    };

    if (key) {
      fetchSetting();
    }
  }, [key]);

  return { value, loading, error };
}

/**
 * Custom hook to fetch all settings for a specific group
 * @param group The group name
 * @returns Object with all settings, loading state, and error
 */
export function useSiteSettings(group?: string) {
  const [settings, setSettings] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const response: any = await siteSettings.getAll(group);
        console.log("[useSiteSettings] Response:", response);
        if (response?.success && response?.data) {
          setSettings(response.data);
          console.log("[useSiteSettings] Settings loaded:", response.data);
        } else if (response?.data) {
          setSettings(response.data);
          console.log("[useSiteSettings] Settings loaded:", response.data);
        } else {
          console.warn(
            "[useSiteSettings] Unexpected response format:",
            response
          );
        }
        setError(null);
      } catch (err) {
        const errorMsg =
          err instanceof Error ? err.message : "Failed to load settings";
        console.error("[useSiteSettings] Error:", errorMsg);
        setError(errorMsg);
        setSettings({});
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [group]);

  return { settings, loading, error };
}

/**
 * Helper function to get a setting value synchronously (must be cached first)
 * This is useful when you want to use settings without hooks
 * @param key The setting key
 * @param defaultValue Default value if setting not found
 * @returns The setting value or default
 */
export async function getSiteSetting(key: string, defaultValue?: any) {
  try {
    const response: any = await siteSettings.get(key);
    return response?.data ?? defaultValue;
  } catch (error) {
    console.error(`Failed to fetch setting ${key}:`, error);
    return defaultValue;
  }
}

/**
 * Helper function to get all settings
 * @param group Optional group filter
 * @returns All settings object
 */
export async function getAllSiteSettings(group?: string) {
  try {
    const response: any = await siteSettings.getAll(group);
    return response?.data ?? {};
  } catch (error) {
    console.error("Failed to fetch settings:", error);
    return {};
  }
}
